import React, { CSSProperties, Fragment } from 'react';
import { IDatalabGridItemProps, IDatalabGridComponent, IDblGridConfig, TColumnWidthModeProp, IColumnItemSizeConfig, TAlignment } from './types';
import { useResponsive } from './hooks/useResponsive';
import { generateGridStyle, generateItemStyle, validateSpan, generateClassName, resolveGridTemplateColumns } from './utils/gridUtils';
import './index.scss';

/**
 * 文本对齐映射常量
 * 将 TAlignment 类型映射到 CSS text-align 值
 */
const TEXT_ALIGN_MAP: Record<TAlignment, 'left' | 'center' | 'right'> = {
  'start': 'left',
  'center': 'center',
  'end': 'right',
  'stretch': 'left',
} as const;

/**
 * 检查是否为 DatalabGrid.Item
 */
const isDatalabGridItem = (child: React.ReactElement): boolean => {
  // 检查是否有 colSpan、rowSpan、align 等 DatalabGrid.Item 特有的 props
  // 并且没有 dblGridConfig（dblGridConfig 是另一种模式）
  return (
    !child.props.dblGridConfig &&
    ('colSpan' in child.props || 'rowSpan' in child.props || 'align' in child.props || 'verticalAlign' in child.props || 'flex' in child.props)
  );
};

/**
 * 获取子元素的列跨度
 */
const getChildColSpan = (child: React.ReactElement): number => {
  // DatalabGrid.Item（通过检查 props 判断）
  if (isDatalabGridItem(child)) {
    return (child.props as IDatalabGridItemProps).colSpan || 1;
  }
  
  // 有 dblGridConfig 的组件
  if (child.props.dblGridConfig) {
    return child.props.dblGridConfig.colSpan || 1;
  }
  
  // 默认不跨列
  return 1;
};

/**
 * 获取列级配置的尺寸值
 */
const getColumnSize = (
  columnItemSize: IColumnItemSizeConfig | undefined,
  columnIndex: number,
  type: 'width' | 'height'
): string | number | undefined => {
  if (!columnItemSize) {
    return undefined;
  }
  
  const configArray = columnItemSize[type];
  if (!configArray || columnIndex >= configArray.length) {
    return undefined;
  }
  return configArray[columnIndex];
};

/**
 * 应用列级尺寸配置到样式
 */
const applyColumnSizeToStyle = (
  style: CSSProperties,
  columnItemSize: IColumnItemSizeConfig | undefined,
  columnIndex: number,
  itemWidth?: string | number,
  itemHeight?: string | number
): void => {
  // 优先级：Item 自身配置 > 列级配置 > 默认值（已在 CSS 中设置）
  
  // 宽度：Item 自身配置优先
  if (itemWidth !== undefined) {
    // eslint-disable-next-line no-param-reassign
    style.width = typeof itemWidth === 'number' ? `${itemWidth}px` : itemWidth;
  } else {
    // 使用列级配置
    const columnWidth = getColumnSize(columnItemSize, columnIndex, 'width');
    if (columnWidth !== undefined) {
      // eslint-disable-next-line no-param-reassign
      style.width = typeof columnWidth === 'number' ? `${columnWidth}px` : columnWidth;
    }
    // 否则使用 CSS 默认值（fit-content）
  }
  
  // 高度：Item 自身配置优先
  if (itemHeight !== undefined) {
    // eslint-disable-next-line no-param-reassign
    style.height = typeof itemHeight === 'number' ? `${itemHeight}px` : itemHeight;
  } else {
    // 使用列级配置
    const columnHeight = getColumnSize(columnItemSize, columnIndex, 'height');
    if (columnHeight !== undefined) {
      // eslint-disable-next-line no-param-reassign
      style.height = typeof columnHeight === 'number' ? `${columnHeight}px` : columnHeight;
    }
    // 否则使用 CSS 默认值（fit-content）
  }
};

/**
 * 生成网格项样式（用于 dblGridConfig）
 */
const generateGridItemStyle = (config: IDblGridConfig): CSSProperties => {
  const style: CSSProperties = {};
  
  if (config.colSpan) {
    style.gridColumn = `span ${config.colSpan}`;
  }
  
  if (config.rowSpan) {
    style.gridRow = `span ${config.rowSpan}`;
  }
  
  if (config.align) {
    style.justifySelf = config.align;
    // 同步设置 text-align
    style.textAlign = TEXT_ALIGN_MAP[config.align];
  }

  if (config.verticalAlign) {
    style.alignSelf = config.verticalAlign;
  }

  if (config.width !== undefined) {
    style.width = typeof config.width === 'number' ? `${config.width}px` : config.width;
  }

  if (config.height !== undefined) {
    style.height = typeof config.height === 'number' ? `${config.height}px` : config.height;
  }
  
  return style;
};

/**
 * DatalabGrid.Item 子组件
 */
const DatalabGridItem: React.FC<IDatalabGridItemProps> = ({
  colSpan,
  rowSpan,
  align,
  verticalAlign,
  width,
  height,
  className,
  style,
  children,
  ...restProps
}) => {
  // 验证跨列跨行参数
  const validation = validateSpan(colSpan, rowSpan);
  if (!validation.isValid) {
    console.warn(`DatalabGrid.Item: ${validation.message}`);
  }

  // 生成样式
  // 优先级：style prop（可能包含列级配置） > 组件 props 中的 width/height > CSS 默认值
  const itemStyle: CSSProperties = {
    ...generateItemStyle(colSpan, rowSpan, align, verticalAlign),
  };
  
  // 先应用 style prop（可能包含列级配置的 width/height）
  if (style) {
    Object.assign(itemStyle, style);
  }
  
  // 如果 style prop 中没有 width/height，再使用组件 props 中的 width/height
  if (itemStyle.width === undefined && width !== undefined) {
    itemStyle.width = typeof width === 'number' ? `${width}px` : width;
  }
  if (itemStyle.height === undefined && height !== undefined) {
    itemStyle.height = typeof height === 'number' ? `${height}px` : height;
  }

  // 生成类名
  const itemClassName = generateClassName(
    'dbl-grid-item',
    {},
    className
  );

  return (
    <div
      {...restProps}
      className={itemClassName}
      style={itemStyle}
    >
      {children}
    </div>
  );
};

/**
 * DatalabGrid 主组件
 */
const DatalabGrid: IDatalabGridComponent = ({
  colCount,
  gap = '16px',
  rowGap,
  className,
  style,
  align = 'start',
  rowHeight = 'auto',
  fixedHeader = false,
  maxHeight,
  maxWidth,
  responsive,
  columnWidthMode = 'minmax',
  verticalAlign = 'center',
  columnItemSize,
  children,
  ...restProps
}) => {
  // 响应式处理
  const { currentColumns } = useResponsive(colCount, responsive);
  // 列索引追踪
  let currentColumnIndex = 0;

  /**
   * 递归处理子元素，支持 Fragment 包裹
   */
  const processChild = (
    child: React.ReactNode,
    index: number
  ): React.ReactNode => {
    if (!React.isValidElement(child)) {
      return child;
    }

    // 处理 Fragment：递归处理其子元素
    if (child.type === Fragment) {
      return React.cloneElement(
        child as React.ReactElement<{ children?: React.ReactNode; }>,
        {},
        React.Children.map(child.props.children, (fragmentChild, fragmentIndex) => {
          return processChild(fragmentChild, index * 1000 + fragmentIndex);
        })
      );
    }

    // 计算当前子元素所在的列索引（使用起始列）
    const colSpan = getChildColSpan(child);
    const startColumnIndex = currentColumnIndex % currentColumns;
    
    // 更新列索引（在计算完 startColumnIndex 之后）
    currentColumnIndex += colSpan;

    // 情况1：DatalabGrid.Item - 应用列级配置
    if (isDatalabGridItem(child)) {
      const itemProps = child.props as IDatalabGridItemProps;
      
      // 先获取 Item 自身可能已经生成的样式（但不包含 width/height，因为会在后面处理）
      const baseItemStyle: CSSProperties = {
        ...generateItemStyle(itemProps.colSpan, itemProps.rowSpan, itemProps.align, itemProps.verticalAlign),
      };
      
      // 如果子项没有设置 align，应用全局 align 对应的 text-align
      if (!itemProps.align && align) {
        baseItemStyle.textAlign = TEXT_ALIGN_MAP[align];
      }
      
      // 应用列级尺寸配置（优先级：Item 自身配置 > 列级配置 > 默认值）
      // 注意：这里需要显式处理 width/height，确保列级配置能生效
      applyColumnSizeToStyle(
        baseItemStyle,
        columnItemSize,
        startColumnIndex,
        itemProps.width,
        itemProps.height
      );
      
      // 合并样式：baseItemStyle（包含列级配置） > itemProps.style（用户自定义样式，优先级最高）
      // 确保 width/height 被正确设置（覆盖 CSS 默认值）
      const finalStyle: CSSProperties = {
        ...baseItemStyle,
        ...(itemProps.style || {}),
      };
      
      // 如果 baseItemStyle 中有 width/height，确保它们被应用到最终样式（覆盖 CSS 默认值和用户自定义样式）
      // 注意：这里需要确保列级配置的 width/height 优先级高于用户自定义的 style
      if (baseItemStyle.width !== undefined) {
        finalStyle.width = baseItemStyle.width;
      }
      if (baseItemStyle.height !== undefined) {
        finalStyle.height = baseItemStyle.height;
      }
      return React.cloneElement(
        child as React.ReactElement<IDatalabGridItemProps>,
        {
          // 不传递 width/height props，避免 DatalabGrid.Item 内部再次处理
          width: undefined,
          height: undefined,
          style: finalStyle,
        }
      );
    }

    // 情况2：有 dblGridConfig 的 React 组件 - 注入网格样式
    if (child.props.dblGridConfig) {
      const config = child.props.dblGridConfig;
      const gridStyle = generateGridItemStyle(config);
      
      // 如果 dblGridConfig 没有设置 align，应用全局 align 对应的 text-align
      if (!config.align && align) {
        gridStyle.textAlign = TEXT_ALIGN_MAP[align];
      }
      
      // 应用列级尺寸配置（优先级：dblGridConfig 配置 > 列级配置 > 默认值）
      applyColumnSizeToStyle(
        gridStyle,
        columnItemSize,
        startColumnIndex,
        config.width,
        config.height
      );
      
      // 排除 dblGridConfig，避免出现在最终 HTML 中
      const { dblGridConfig, ...cleanProps } = child.props;
      
      return React.cloneElement(
        child as React.ReactElement<{
          style?: CSSProperties;
          className?: string;
          [key: string]: unknown;
        }>,
        {
          ...cleanProps,
          dblGridConfig: undefined,
          style: {
            ...gridStyle,
            ...(child.props.style || {}),
          },
        },
      );
    }

    // 情况3：没有 dblGridConfig 的 React 组件 - 仅应用列级配置（不添加 dbl-grid-item 类）
    const existingClassName = child.props.className || '';
    
    const defaultStyle: CSSProperties = {};
    
    // 应用全局 align 对应的 text-align
    if (align) {
      defaultStyle.textAlign = TEXT_ALIGN_MAP[align];
    }
    
    // 应用列级尺寸配置（优先级：列级配置 > 默认值）
    applyColumnSizeToStyle(
      defaultStyle,
      columnItemSize,
      startColumnIndex
    );
    
    return React.cloneElement(
      child as React.ReactElement<{
        className?: string;
        style?: CSSProperties;
        [key: string]: unknown;
      }>,
      {
        className: existingClassName,
        style: {
          ...defaultStyle,
          ...(child.props.style || {}),
        },
      },
    );
  };

  // 处理子元素，实现混合模式（支持 Fragment）
  const processedChildren = React.Children.map(children, (child, index) => {
    return processChild(child, index);
  });

  const { gridTemplateColumns: userTemplate, ...restStyle } = style || {};

  const gridTemplateColumns = resolveGridTemplateColumns(
    columnWidthMode as TColumnWidthModeProp,
    currentColumns,
    userTemplate as string | undefined
  );

  // 生成网格样式
  const gridStyle: CSSProperties = {
    ...generateGridStyle(
      currentColumns,
      gap,
      align,
      rowHeight,
      maxHeight,
      maxWidth,
      columnWidthMode,
      verticalAlign,
      gridTemplateColumns,
      rowGap
    ),
    ...restStyle,
  };

  // 生成类名
  const gridClassName = generateClassName(
    'dbl-grid',
    {
      'fixed-header': fixedHeader,
      'scrollable': !!(maxHeight || maxWidth),
    },
    className
  );

  return (
    <div
      {...restProps}
      className={gridClassName}
      style={gridStyle}
    >
      {processedChildren}
    </div>
  );
};

// 设置子组件
DatalabGrid.Item = DatalabGridItem;

export default DatalabGrid;
